import { useState, useEffect, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Package, X, Plus, Edit, AlertCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getAllItems, createItem, updateItem, deleteItem } from '../utils/itemApi';
import { getAllAccounts } from '../utils/accountApi';
import { getAllItemCategories, createItemCategory } from '../utils/itemCategoryApi';
import { ApiValidationError } from '../types/auth';
import type { Item, CreateItemData, ItemType, UnitOfMeasure } from '../types/item';
import type { GlAccount } from '../types/account';
import type { ItemCategory } from '../types/itemCategory';
import { getDefaultGlAccountsForItemType } from '../utils/itemGlDefaults';
import { formatCurrency, formatQuantity } from '../utils/formatCurrency';
import { hasOpenBackorders } from '../utils/negativeInventory';
import { toast, toastApiError } from '../utils/toast';
import './ProductsManage.css';

const ITEM_TYPES: ItemType[] = ['INVENTORY', 'SERVICE', 'NON_INVENTORY'];
const UNITS_OF_MEASURE: UnitOfMeasure[] = ['UNIT', 'BOX', 'SET', 'PAIR', 'KG', 'LITRE', 'METRE'];

interface FieldErrors {
  [key: string]: string[] | undefined;
}

const parsePriceField = (value: string) => {
  if (!value.trim()) return 0;
  const num = parseFloat(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
};

interface ItemFormData {
  sku: string;
  name: string;
  description: string;
  item_type: ItemType;
  unit_of_measure: UnitOfMeasure;
  unit_price: string;
  standard_cost: string;
  dealer_selling_price: string;
  reorder_point: string;
  taxable: boolean;
  active: boolean;
  income_account_id: string;
  expense_account_id: string;
  asset_account_id: string;
  category_id: string;
}

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const canManageItems = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteItem = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const FormSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
    <div className="border-b border-gray-100 pb-3">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {description && <p className="mt-1 text-xs text-gray-500 leading-relaxed">{description}</p>}
    </div>
    {children}
  </section>
);

const defaultFormData = (): ItemFormData => ({
  sku: '',
  name: '',
  description: '',
  item_type: 'INVENTORY',
  unit_of_measure: 'UNIT',
  unit_price: '0',
  standard_cost: '0',
  dealer_selling_price: '0',
  reorder_point: '0',
  taxable: false,
  active: true,
  income_account_id: '',
  expense_account_id: '',
  asset_account_id: '',
  category_id: '',
});

const toPayload = (form: ItemFormData): CreateItemData => ({
  sku: form.sku.trim(),
  name: form.name.trim(),
  description: form.description.trim() || null,
  item_type: form.item_type,
  unit_of_measure: form.unit_of_measure,
  unit_price: parsePriceField(form.unit_price),
  standard_cost: parsePriceField(form.standard_cost),
  dealer_selling_price: parsePriceField(form.dealer_selling_price),
  reorder_point: parseFloat(form.reorder_point) || 0,
  taxable: form.taxable,
  active: form.active,
  income_account_id: form.income_account_id ? Number(form.income_account_id) : null,
  expense_account_id: form.expense_account_id ? Number(form.expense_account_id) : null,
  asset_account_id: form.asset_account_id ? Number(form.asset_account_id) : null,
  category_id: form.category_id ? Number(form.category_id) : null,
});

const itemToFormData = (item: Item): ItemFormData => ({
  sku: item.sku,
  name: item.name,
  description: item.description ?? '',
  item_type: item.item_type,
  unit_of_measure: item.unit_of_measure,
  unit_price: item.unit_price,
  standard_cost: item.standard_cost ?? '0',
  dealer_selling_price: item.dealer_selling_price ?? '0',
  reorder_point: item.reorder_point,
  taxable: item.taxable,
  active: item.active,
  income_account_id: item.income_account_id ? String(item.income_account_id) : '',
  expense_account_id: item.expense_account_id ? String(item.expense_account_id) : '',
  asset_account_id: item.asset_account_id ? String(item.asset_account_id) : '',
  category_id: item.category_id ? String(item.category_id) : '',
});

const ProductsManage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<ItemFormData>(defaultFormData());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!localStorage.getItem('token')) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      const [itemsData, accountsData, categoriesData] = await Promise.all([
        getAllItems(),
        canManageItems() ? getAllAccounts().catch(() => []) : Promise.resolve([]),
        getAllItemCategories({ active: true }).catch(() => []),
      ]);

      setItems(itemsData);
      setAccounts(accountsData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateCategory = () => {
    setShowCreateCategory(false);
    setNewCategoryName('');
    setNewCategoryParentId('');
    setCategoryError(null);
  };

  const resetForm = () => {
    setFormData(defaultFormData());
    setFieldErrors({});
    setModalError(null);
    resetCreateCategory();
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingItem(null);
    resetForm();
  };

  const openCreateModal = () => {
    resetForm();
    const base = defaultFormData();
    const glDefaults = getDefaultGlAccountsForItemType(base.item_type, accounts);
    setFormData({ ...base, ...glDefaults });
    setShowCreateModal(true);
    if (categories.length === 0 && canManageItems()) {
      setShowCreateCategory(true);
    }
  };

  const openEditModal = (item: Item) => {
    setEditingItem(item);
    setFormData(itemToFormData(item));
    setFieldErrors({});
    setModalError(null);
    setShowEditModal(true);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      await createItem(toPayload(formData));
      setShowCreateModal(false);
      resetForm();
      await loadData();
      toast.success('Item created successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to create item');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    setModalError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      await updateItem(editingItem.id, toPayload(formData));
      setShowEditModal(false);
      setEditingItem(null);
      resetForm();
      await loadData();
      toast.success('Item updated successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to update item');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId: number) => {
    if (!canDeleteItem()) {
      toast.error('Only administrators can delete items.');
      return;
    }

    const item = items.find((row) => row.id === itemId);
    if (item && hasOpenBackorders(item)) {
      toast.error('Cannot delete an item with open backorders.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await deleteItem(itemId);
      await loadData();
      toast.success('Item deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete item');
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedCategories = [...categories]
    .filter((c) => c.active)
    .sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name));

  const parentCategoryOptions = sortedCategories.filter((c) => c.level < 3);

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setCategoryError('Category name is required.');
      return;
    }

    setCategoryError(null);
    setCreatingCategory(true);

    try {
      const created = await createItemCategory({
        name,
        parent_category_id: newCategoryParentId ? Number(newCategoryParentId) : null,
        active: true,
      });
      setCategories((prev) =>
        [...prev, created].sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name))
      );
      setFormData((prev) => ({ ...prev, category_id: String(created.id) }));
      resetCreateCategory();
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const nameErr = err.errors.name?.[0];
        const parentErr = err.errors.parent_category_id?.[0];
        setCategoryError(nameErr || parentErr || err.message);
      } else {
        setCategoryError(err instanceof Error ? err.message : 'Failed to create category');
      }
    } finally {
      setCreatingCategory(false);
    }
  };

  const applySuggestedGlAccounts = () => {
    const defaults = getDefaultGlAccountsForItemType(formData.item_type, accounts);
    setFormData((prev) => ({ ...prev, ...defaults }));
  };

  const handleItemTypeChange = (itemType: ItemType, isEdit: boolean) => {
    setFormData((prev) => {
      const next = { ...prev, item_type: itemType };
      if (!isEdit) {
        return { ...next, ...getDefaultGlAccountsForItemType(itemType, accounts) };
      }
      return next;
    });
  };

  const incomeAccounts = accounts.filter((a) => a.account_type === 'INCOME' && a.active);
  const expenseAccounts = accounts.filter((a) => a.account_type === 'EXPENSE' && a.active);
  const assetAccounts = accounts.filter((a) => a.account_type === 'ASSET' && a.active);

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const renderAccountSelect = (
    label: string,
    value: string,
    onChange: (val: string) => void,
    options: GlAccount[],
    hint?: string
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
      >
        <option value="">None</option>
        {options.map((account) => (
          <option key={account.id} value={account.id}>
            {account.account_number} — {account.name}
          </option>
        ))}
      </select>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );

  const renderItemForm = (onSubmit: (e: FormEvent) => void, submitLabel: string, editing?: Item) => (
    <form onSubmit={onSubmit} className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
      {modalError && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{modalError}</span>
        </div>
      )}

      <FormSection
        title="Basic information"
        description="Core identifiers, catalog notes, and reference pricing. Standard cost and dealer price are informational only."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={80}
              value={formData.sku}
              onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${
                fieldError('sku') ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="WDG-001"
            />
            {fieldError('sku') && <p className="mt-1 text-sm text-red-600">{fieldError('sku')}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={250}
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                fieldError('name') ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Test Widget"
            />
            {fieldError('name') && <p className="mt-1 text-sm text-red-600">{fieldError('name')}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            rows={3}
            maxLength={5000}
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className={`w-full border rounded-lg px-3 py-2 text-sm ${
              fieldError('description') ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Heavy-duty widget for industrial use."
          />
          {fieldError('description') && (
            <p className="mt-1 text-sm text-red-600">{fieldError('description')}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit price (retail) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              required
              min={0}
              step="any"
              value={formData.unit_price}
              onChange={(e) => setFormData((prev) => ({ ...prev, unit_price: e.target.value }))}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                fieldError('unit_price') ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {fieldError('unit_price') && (
              <p className="mt-1 text-sm text-red-600">{fieldError('unit_price')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Standard cost</label>
            <input
              type="number"
              min={0}
              step="any"
              value={formData.standard_cost}
              onChange={(e) => setFormData((prev) => ({ ...prev, standard_cost: e.target.value }))}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                fieldError('standard_cost') ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {fieldError('standard_cost') && (
              <p className="mt-1 text-sm text-red-600">{fieldError('standard_cost')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dealer price</label>
            <input
              type="number"
              min={0}
              step="any"
              value={formData.dealer_selling_price}
              onChange={(e) => setFormData((prev) => ({ ...prev, dealer_selling_price: e.target.value }))}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                fieldError('dealer_selling_price') ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {fieldError('dealer_selling_price') && (
              <p className="mt-1 text-sm text-red-600">{fieldError('dealer_selling_price')}</p>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection
        title="Classification"
        description="Group the item in your catalog and define how it is sold or tracked."
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData((prev) => ({ ...prev, category_id: e.target.value }))}
            className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
              fieldError('category_id') ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">None (uncategorized)</option>
            {sortedCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.fully_qualified_name}
              </option>
            ))}
          </select>
          {fieldError('category_id') && (
            <p className="mt-1 text-sm text-red-600">{fieldError('category_id')}</p>
          )}
          {canManageItems() && (
            <div className="mt-2">
              {!showCreateCategory ? (
                <button
                  type="button"
                  onClick={() => setShowCreateCategory(true)}
                  className="text-sm text-slate-700 hover:text-slate-900 underline"
                >
                  + Create new category
                </button>
              ) : (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    New category
                  </p>
                  {categoryError && (
                    <p className="text-sm text-red-600 flex items-start gap-1.5">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      {categoryError}
                    </p>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="e.g. Hardware"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent category
                    </label>
                    <select
                      value={newCategoryParentId}
                      onChange={(e) => setNewCategoryParentId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="">Top level (no parent)</option>
                      {parentCategoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.fully_qualified_name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Categories support up to 3 levels deep.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={creatingCategory}
                      className="px-3 py-1.5 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                    >
                      {creatingCategory ? 'Creating...' : 'Create & select'}
                    </button>
                    <button
                      type="button"
                      onClick={resetCreateCategory}
                      disabled={creatingCategory}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {sortedCategories.length === 0 && !showCreateCategory && (
            <p className="mt-1 text-xs text-gray-500">No categories yet — create one to organize this item.</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
            <select
              value={formData.item_type}
              onChange={(e) => handleItemTypeChange(e.target.value as ItemType, Boolean(editing))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {ITEM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {formatLabel(type)}
                </option>
              ))}
            </select>
            {!editing && (
              <p className="mt-1 text-xs text-gray-500">
                Changing type updates suggested GL accounts for new items.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
            <select
              value={formData.unit_of_measure}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  unit_of_measure: e.target.value as UnitOfMeasure,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {UNITS_OF_MEASURE.map((unit) => (
                <option key={unit} value={unit}>
                  {formatLabel(unit)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormSection>

      <FormSection
        title="Inventory & status"
        description="Stock thresholds and whether this item is active or taxable."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Point</label>
            <input
              type="number"
              min={0}
              step="any"
              value={formData.reorder_point}
              onChange={(e) => setFormData((prev) => ({ ...prev, reorder_point: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {editing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qty on Hand</label>
              <input
                type="text"
                readOnly
                value={formatQuantity(editing.qty_on_hand)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">System-managed — updated by bills and invoices</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formData.taxable}
              onChange={(e) => setFormData((prev) => ({ ...prev, taxable: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300"
            />
            Taxable
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300"
            />
            Active
          </label>
        </div>
      </FormSection>

      {canManageItems() && accounts.length > 0 && (
        <FormSection
          title="GL accounts"
          description="Link ledger accounts used when this item is sold, purchased, or stocked. On new items, accounts are auto-selected from your chart of accounts by sub-type."
        >
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 space-y-2">
            <p className="font-medium">How auto-selection works</p>
            <ul className="list-disc pl-4 space-y-1 text-blue-800">
              <li>
                <strong>Income</strong> → first active account with sub-type <code className="font-mono">SALES_REVENUE</code>
              </li>
              <li>
                <strong>Inventory asset</strong> → <code className="font-mono">INVENTORY_ASSET</code> (inventory items only)
              </li>
              <li>
                <strong>Expense / COGS</strong> → <code className="font-mono">COGS</code> (inventory &amp; non-inventory)
              </li>
            </ul>
            <p className="text-blue-700">
              Ensure those sub-types exist under Accounts. New items pick defaults when opened; change item type to refresh suggestions.
            </p>
            <button
              type="button"
              onClick={applySuggestedGlAccounts}
              className="text-blue-900 underline hover:text-blue-950"
            >
              Apply suggested accounts now
            </button>
          </div>

          {renderAccountSelect(
            'Income Account',
            formData.income_account_id,
            (val) => setFormData((prev) => ({ ...prev, income_account_id: val })),
            incomeAccounts,
            'Sales revenue account (SALES_REVENUE)'
          )}
          {(formData.item_type === 'INVENTORY' || formData.item_type === 'NON_INVENTORY') &&
            renderAccountSelect(
              formData.item_type === 'INVENTORY' ? 'Asset Account' : 'Expense Account',
              formData.item_type === 'INVENTORY'
                ? formData.asset_account_id
                : formData.expense_account_id,
              (val) =>
                setFormData((prev) =>
                  formData.item_type === 'INVENTORY'
                    ? { ...prev, asset_account_id: val }
                    : { ...prev, expense_account_id: val }
                ),
              formData.item_type === 'INVENTORY' ? assetAccounts : expenseAccounts,
              formData.item_type === 'INVENTORY'
                ? 'Inventory asset account (INVENTORY_ASSET)'
                : 'Expense / COGS account (COGS)'
            )}
          {formData.item_type === 'INVENTORY' &&
            renderAccountSelect(
              'Expense Account (optional)',
              formData.expense_account_id,
              (val) => setFormData((prev) => ({ ...prev, expense_account_id: val })),
              expenseAccounts,
              'COGS account — optional at create'
            )}
        </FormSection>
      )}

      <div className="flex justify-end gap-3 pt-2 sticky bottom-0 bg-white border-t border-gray-100">
        <button
          type="button"
          onClick={closeModal}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
        >
          {submitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white shadow border-b border-gray-200">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Item Catalog</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage inventory, services, and non-inventory items
                </p>
              </div>

              {canManageItems() && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-slate-800 hover:bg-slate-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Item
                </button>
              )}
            </div>

            <div className="mt-4">
              <button
                onClick={() => navigate('/products')}
                className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md text-slate-700 bg-slate-50 hover:bg-slate-100"
              >
                <Package className="h-3.5 w-3.5 mr-1.5" />
                View All Products
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start justify-between gap-4">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
              <button onClick={loadData} className="text-sm text-red-700 underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Items</h2>
                <p className="mt-1 text-sm text-gray-500">Sorted by name</p>
              </div>

              <div className="w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-slate-800 focus:border-slate-600"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-5 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">UoM</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">On Hand</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    {canManageItems() && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">{item.sku}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatLabel(item.item_type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatLabel(item.unit_of_measure)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatQuantity(item.qty_on_hand)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex w-fit px-2 py-0.5 text-xs font-medium rounded-full ${
                              item.active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {item.active ? 'Active' : 'Inactive'}
                          </span>
                          {item.taxable && (
                            <span className="inline-flex w-fit px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                              Taxable
                            </span>
                          )}
                        </div>
                      </td>
                      {canManageItems() && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="text-slate-600 hover:text-slate-900"
                              title="Edit item"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {canDeleteItem() && (
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Delete item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredItems.length === 0 && (
                <div className="text-center py-16">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-3 text-sm font-medium text-gray-900">No items found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {searchQuery ? 'Try adjusting your search.' : 'Get started by adding an item.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Item</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderItemForm(handleCreate, 'Create Item')}
          </div>
        </div>
      )}

      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Item</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderItemForm(handleUpdate, 'Save Changes', editingItem)}
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductsManage;
